import { action, atom, reatomBoolean, sleep } from '@reatom/framework'
import { withLocalStorage } from '@reatom/persist-web-storage'
import i18next from 'i18next'

import { Result } from '@blade/std-lib'
import { Button } from '@blade/ui-kit'
import { isInitializationReadyAtom } from '@root/features/initialization/atoms/common'
import { environment, PlatformUtil } from '@root/shared'
import { showSnackbarAction } from '@root/shared/ui-kit'

import * as Api from '../api'
import { UpdateState, DownloadState } from '../model'

import { isOpenUpdateModalAtom } from './update-modal'

const getCheckUpdatePeriod = (defaultPeriodMillis: number) => {
  const envData = Number(environment.checkUpdatePeriod)
  if (Number.isNaN(envData) || !Number.isFinite(envData) || envData <= 0) {
    return defaultPeriodMillis
  }

  return envData
}

const checkUpdatePeriod = getCheckUpdatePeriod(30 * 1000)

export const updateStateAtom = atom<UpdateState.UpdateState>(
  new UpdateState.UpdateCheck(),
  'updateStateAtom',
)

export const hasToCheckUpdatesAtom = reatomBoolean(
  true,
  'hasToCheckUpdatesAtom',
)

export const lastShownVersionAtom = atom<string | undefined>(
  undefined,
  'lastShownVersionAtom',
).pipe(withLocalStorage('update.lastShownVersion'))

export const startCheckingUpdateAction = action(async (ctx) => {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  while (true) {
    if (ctx.get(hasToCheckUpdatesAtom)) {
      updateStateAtom(ctx, new UpdateState.UpdateCheck())
      const updateResult = await ctx.schedule(() =>
        PlatformUtil.check({ timeout: 5000 }),
      )

      if (Result.isSuccess(updateResult)) {
        const update = updateResult.success
        if (update) {
          const lastShownVersion = ctx.get(lastShownVersionAtom)

          if (lastShownVersion !== update.version) {
            const showSnackbar = () =>
              showSnackbarAction(ctx, {
                delay: 'permanent',
                text: i18next.t('update.new_version_available', {
                  version: update.version,
                }),
                intent: 'info',
                onClose: () => lastShownVersionAtom(ctx, update.version),
                description: ({ onClose }) => (
                  <Button
                    type="button"
                    size="xxs"
                    radius="rounded"
                    color="primary"
                    onClick={() => {
                      onClose?.()
                      isOpenUpdateModalAtom.setTrue(ctx)
                    }}
                  >
                    {i18next.t('common.open')}
                  </Button>
                ),
              })
            if (ctx.get(isInitializationReadyAtom)) {
              showSnackbar()
            } else {
              const unsub = ctx.subscribe(
                isInitializationReadyAtom,
                (isReady) => {
                  if (isReady) {
                    showSnackbar()
                    unsub()
                  }
                },
              )
            }
          }

          updateStateAtom(ctx, new UpdateState.UpdateAvailable({ update }))
          break
        } else {
          updateStateAtom(ctx, new UpdateState.LatestUpdate())
        }
      } else {
        updateStateAtom(
          ctx,
          new UpdateState.UpdateCheckError({ cause: updateResult.failure }),
        )
      }
    }

    await ctx.schedule(() => sleep(checkUpdatePeriod))
  }
}, 'startCheckingUpdateAction')

export const downloadStateAtom = atom<DownloadState.DownloadState>(
  new DownloadState.DownloadStateInitial({}),
  'downloadStateAtom',
)

export const downloadUpdateAction = action(async (ctx) => {
  const state = ctx.get(updateStateAtom)

  if (
    state._tag !== 'UpdateAvailable' &&
    state._tag !== 'UpdateDownloadError'
  ) {
    return
  }

  const downloadUpdateResult = await ctx.schedule(() =>
    Api.downloadUpdate(state.update, {
      onStarted: (_) =>
        downloadStateAtom(
          ctx,
          new DownloadState.DownloadStateInitial({
            contentLength: _.contentLength,
          }),
        ),
      onProgress: (_) =>
        downloadStateAtom(ctx, DownloadState.addDownloaded(_.chunkLength)),
      onFinished: () => {
        downloadStateAtom(ctx, DownloadState.finish)
      },
    }),
  )

  if (Result.isFailure(downloadUpdateResult)) {
    updateStateAtom(
      ctx,
      new UpdateState.UpdateDownloadError({
        cause: downloadUpdateResult.failure,
        update: state.update,
      }),
    )
  } else {
    updateStateAtom(ctx, new UpdateState.UpdateReady({ update: state.update }))
  }
}, 'downloadUpdateAction')

export const installAndRelaunchUpdateAction = action(async (ctx) => {
  const state = ctx.get(updateStateAtom)

  if (state._tag !== 'UpdateReady' && state._tag !== 'UpdateInstallError') {
    return
  }

  updateStateAtom(
    ctx,
    new UpdateState.UpdateInstallation({ update: state.update }),
  )

  const installResult = await ctx.schedule(() => state.update.install())

  if (Result.isFailure(installResult)) {
    updateStateAtom(
      ctx,
      new UpdateState.UpdateInstallError({
        cause: installResult.failure,
        update: state.update,
      }),
    )
  }

  const relaunchResult = await ctx.schedule(() => Api.relaunchApplication())

  if (Result.isFailure(relaunchResult)) {
    updateStateAtom(
      ctx,
      new UpdateState.UpdateRelaunchError({ cause: relaunchResult.failure }),
    )
  }

  updateStateAtom(ctx, new UpdateState.UpdateCheck())
}, 'installAndRelaunchUpdateAction')
